/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import styled from 'styled-components'
import { AdjustmentsIcon, IdentificationIcon, PencilIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { setCartItem, setCartOpen, toggleAutoRenew, toggleIDProtection } from 'store/slices/shopSlice'
import { CartItem } from 'models/shop'
import { useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    key: number,
    item: CartItem,
    option: string | null
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function CartConfigOption({ item, option }: Props) {
    const dispatch = useAppDispatch()
    const { pathname } = useLocation()
    const { cart, cartUpdateStatus } = useAppSelector((state) => state.shop)
    const [updateCartItem] = useUpdateCartItemsMutation()

    function synchroniseWithCart(config = {}) {
        if (cart) {
            const updateItems = cart.items.map((cartItem: CartItem) => {
                if (cartItem.product_id === item.product_id && cartItem.name === item.name) {
                    return {
                        ...cartItem,
                        ...config
                    }
                }

                return cartItem
            })

            updateCartItem({ uuid: cart.uuid, items: updateItems })
        }
    }

    switch (option) {
        case 'auto_renew':
            return (
                <Option>
                    <Label>
                        <Icon>
                            <AdjustmentsIcon/>
                        </Icon>
                        <Name>
                            Auto-Renew
                        </Name>
                        {pathname !== '/order'
                            ? (
                                <NXUIForm.Switch
                                    checked={item.auto_renew}
                                    disabled={cartUpdateStatus === 'loading'}
                                    onClick={() => {
                                        dispatch(toggleAutoRenew({
                                            product_id: item.product_id,
                                            name: item.name,
                                            auto_renew: item.auto_renew
                                        }))
                                        synchroniseWithCart({ auto_renew: !item.auto_renew })
                                    }}
                                />
                            )
                            : item.auto_renew ? 'Enabled' : 'Disabled'
                        }
                    </Label>
                </Option>
            )
        case 'id_protection':
            return (
                <Option>
                    <Label>
                        <Icon>
                            <AdjustmentsIcon/>
                        </Icon>
                        <Name>
                            ID Protection
                        </Name>
                        {pathname !== '/order'
                            ? (
                                <NXUIForm.Switch
                                    checked={item.id_protection}
                                    onClick={() => {
                                        dispatch(toggleIDProtection({
                                            product_id: item.product_id,
                                            name: item.name,
                                            id_protection: item.id_protection
                                        }))
                                        synchroniseWithCart({ id_protection: !item.id_protection })
                                    }}
                                />
                            )
                            : item.id_protection ? 'Enabled' : 'Disabled'
                        }

                    </Label>
                </Option>
            )
        case 'domain_contact_fields':
            return (
                <>
                    {pathname !== '/order'
                        ? (
                            <Option>
                                <Label>
                                    <Icon>
                                        <IdentificationIcon/>
                                    </Icon>
                                    <span>Domain Contact</span>
                                    <button
                                        type={'button'}
                                        onClick={() => {
                                            if (pathname === '/checkout') {
                                                dispatch(setCartOpen(true))
                                            }
                                            dispatch(setCartItem({ template: 'contact', item }))
                                        }}
                                    >
                                        <Icon>
                                            <PencilIcon/>
                                        </Icon>
                                    </button>
                                </Label>
                            </Option>
                        )
                        : null
                    }
                </>
            )
        // case 'custom_fields':
        //     return (
        //         item.custom_fields.map((custom_field) => (
        //             <Option key={custom_field.input_id}>
        //                 <Label>
        //                     <Icon>
        //                         <IdentificationIcon/>
        //                     </Icon>
        //                     <Name>
        //                         {custom_field.value}
        //                     </Name>
        //                 </Label>
        //             </Option>
        //         ))
        //     )
        case 'epp':
            return (
                <Option>
                    <Label>
                        <Icon>
                            <IdentificationIcon/>
                        </Icon>
                        <span>EPP Code</span>
                        <Name>
                            {item.epp}
                        </Name>
                    </Label>
                </Option>
            )
        default:
            return null
    }
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Option = styled.div`
    display: flex;
    padding: 8px 0;
`

const Label = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const Name = styled.div`
    color: ${props => props.theme['text--100']};
`

const Icon = styled.div`
    > svg {
        width: 16px;
        height: 16px;
        color: ${props => props.theme['icon--100']}
    }
`
